import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Progress } from '@backstage/core-components';
import {
  type DiscoveryApi,
  discoveryApiRef,
  type FetchApi,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import {
  type FieldExtensionComponentProps,
  type FieldSchema,
  makeFieldSchema,
} from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import { Button, Text, TextField } from '@backstage/ui';
import {
  CheckCircleIcon,
  ErrorOutlineIcon,
  HelpOutlineIcon,
} from '@ns/plugin-ns-theme';
import {
  type AsyncResolverState,
  useAsyncResolver,
} from '@ns/plugin-react-async-utils';

import {
  createTessaEnvironmentAvailabilityClient,
  type TessaEnvironmentAvailability,
} from './tessa-environment-availability-client';

type WorkspaceFieldKind = 'key' | 'name';
type WorkspaceFieldProps = FieldExtensionComponentProps<unknown>;
type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'taken' | 'error';
type WorkspaceAvailabilityContext = {
  kind: WorkspaceFieldKind;
  value: string;
};

const AVAILABILITY_DEBOUNCE_MS = 400;
const AVAILABILITY_RETRY = {
  baseDelayMs: 300,
  honorRetryAfter: true,
  maxAttempts: 3,
};
const MAX_WORKSPACE_KEY_CHECKS = 8;
const WORKSPACE_KEY_SUFFIXES = [
  '1',
  '2',
  'A',
  'B',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  ...'CDEFGHIJKLMNOPQRSTUVWXYZ',
];

const workspaceFieldSchema: FieldSchema<
  string,
  Record<string, never>
> = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z => z.object({}),
});

export function isValidWorkspaceKey(value: string): boolean {
  return /^[A-Z][A-Z0-9]{2,7}$/.test(value);
}

export function isValidWorkspaceName(value: string): boolean {
  return (
    value.length >= 3 &&
    value.length <= 25 &&
    /^[A-Za-z0-9](?:[A-Za-z0-9 ]*[A-Za-z0-9])?$/.test(value)
  );
}

function endOfUppercasePart(word: string, start: number): number {
  let end = start + 1;
  while (end < word.length && /[A-Z]/.test(word.charAt(end))) end += 1;
  if (end < word.length && /[a-z]/.test(word.charAt(end))) {
    if (end - start > 1) return end - 1;
    while (end < word.length && /[a-z]/.test(word.charAt(end))) end += 1;
  }
  return end;
}

function endOfLowercaseOrDigitPart(word: string, start: number): number {
  const characterClass = /[a-z]/.test(word.charAt(start)) ? /[a-z]/ : /\d/;
  let end = start + 1;
  while (end < word.length && characterClass.test(word.charAt(end))) end += 1;
  return end;
}

function splitWorkspaceWord(word: string): string[] {
  const parts: string[] = [];
  let start = 0;
  while (start < word.length) {
    const end = /[A-Z]/.test(word.charAt(start))
      ? endOfUppercasePart(word, start)
      : endOfLowercaseOrDigitPart(word, start);
    parts.push(word.slice(start, end));
    start = end;
  }
  return parts;
}

function splitWorkspaceName(value: string): string[] {
  return value.trim().split(/\s+/).flatMap(splitWorkspaceWord);
}

export function getWorkspaceKeyFromName(
  workspaceName: string,
): string | undefined {
  const parts = splitWorkspaceName(workspaceName);
  const firstLetterPartIndex = parts.findIndex(part => /[A-Za-z]/.test(part));

  if (firstLetterPartIndex === -1) {
    const digits = parts.join('').slice(0, 7);
    const numericKey = `W${digits}`;
    return isValidWorkspaceKey(numericKey) ? numericKey : undefined;
  }

  const firstLetterPart = parts[firstLetterPartIndex];
  if (firstLetterPart === undefined) return undefined;
  const initials = parts
    .slice(firstLetterPartIndex)
    .map(part => part[0])
    .join('');
  let key = `${firstLetterPart.slice(0, 2)}${initials.slice(1)}`.toUpperCase();

  for (const character of parts.join('').slice(2)) {
    if (key.length >= 3) break;
    key += character.toUpperCase();
  }

  const normalizedKey = (key.length < 3 ? `W${parts.join('')}` : key)
    .toUpperCase()
    .slice(0, 8);

  return isValidWorkspaceKey(normalizedKey) ? normalizedKey : undefined;
}

export function getWorkspaceKeyCandidates(workspaceName: string): string[] {
  const baseKey = getWorkspaceKeyFromName(workspaceName);
  if (!baseKey) return [];

  return [
    baseKey,
    ...WORKSPACE_KEY_SUFFIXES.map(
      suffix => `${baseKey.slice(0, 8 - suffix.length)}${suffix}`,
    ),
  ].filter(
    (candidate, index, candidates) =>
      isValidWorkspaceKey(candidate) && candidates.indexOf(candidate) === index,
  );
}

type WorkspaceAvailabilityCheck = (input: {
  environmentKey?: string;
  environmentName?: string;
  signal?: AbortSignal;
}) => Promise<TessaEnvironmentAvailability>;

export async function findAvailableWorkspaceKey(input: {
  checkAvailability: WorkspaceAvailabilityCheck;
  signal?: AbortSignal;
  workspaceName: string;
}): Promise<string | undefined> {
  for (const candidate of getWorkspaceKeyCandidates(input.workspaceName).slice(
    0,
    MAX_WORKSPACE_KEY_CHECKS,
  )) {
    const availability = await input.checkAvailability({
      environmentKey: candidate,
      signal: input.signal,
    });
    if (availability.key?.available) return candidate;
  }

  return undefined;
}

function getFieldValue(
  kind: WorkspaceFieldKind,
  availability: TessaEnvironmentAvailability,
): boolean {
  const result = kind === 'key' ? availability.key : availability.name;
  return result !== null && !result.available;
}

function getInputLabel(kind: WorkspaceFieldKind): string {
  return kind === 'key' ? 'Workspace key' : 'Workspace name';
}

function normalizeRawErrors(rawErrors: string[] | undefined): string[] {
  return rawErrors ?? [];
}

function getAvailabilityMessage(
  kind: WorkspaceFieldKind,
  status: AvailabilityStatus,
): string | undefined {
  if (status === 'checking') {
    return `Checking ${getInputLabel(kind).toLowerCase()} availability...`;
  }
  if (status === 'available') {
    return `This ${getInputLabel(kind).toLowerCase()} is available.`;
  }
  if (status === 'taken') {
    return `This ${getInputLabel(
      kind,
    ).toLowerCase()} is already in use. Choose a different ${kind}.`;
  }
  if (status === 'error') {
    return "Availability couldn't be checked right now. We'll check again when you select Review.";
  }
  return undefined;
}

function getWorkspaceSuggestions(
  kind: WorkspaceFieldKind,
  value: string,
  workspaceName: string,
): string[] {
  if (kind === 'key') {
    const candidates = isValidWorkspaceName(workspaceName)
      ? getWorkspaceKeyCandidates(workspaceName)
      : WORKSPACE_KEY_SUFFIXES.map(
          suffix => `${value.slice(0, 8 - suffix.length)}${suffix}`,
        );
    return candidates
      .filter(candidate => candidate !== value)
      .filter(isValidWorkspaceKey);
  }

  const prefix = value.slice(0, 23).trimEnd();
  return ['1', '2', 'A', 'B']
    .map(suffix => `${prefix} ${suffix}`)
    .filter(isValidWorkspaceName);
}

async function findAvailableWorkspaceSuggestions(input: {
  checkAvailability: WorkspaceAvailabilityCheck;
  kind: WorkspaceFieldKind;
  signal: AbortSignal;
  value: string;
  workspaceName: string;
}): Promise<string[]> {
  const available: string[] = [];
  for (const candidate of getWorkspaceSuggestions(
    input.kind,
    input.value,
    input.workspaceName,
  ).slice(0, MAX_WORKSPACE_KEY_CHECKS)) {
    if (input.signal.aborted) break;
    const result = await input.checkAvailability(
      input.kind === 'key'
        ? { environmentKey: candidate, signal: input.signal }
        : { environmentName: candidate, signal: input.signal },
    );
    if ((input.kind === 'key' ? result.key : result.name)?.available) {
      available.push(candidate);
    }
    if (available.length === 4) break;
  }
  return available;
}

function getStatusIcon(
  status: AvailabilityStatus,
  hasFormatError: boolean,
): ReactNode {
  if (hasFormatError || status === 'taken') {
    return <ErrorOutlineIcon color="error" fontSize="small" />;
  }
  if (status === 'checking') {
    return (
      <Progress aria-label="Checking availability" style={{ width: 18 }} />
    );
  }
  if (status === 'available') {
    return <CheckCircleIcon color="primary" fontSize="small" />;
  }
  if (status === 'error') {
    return <HelpOutlineIcon color="error" fontSize="small" />;
  }
  return undefined;
}

function getRetryOption(input: {
  autoKeySelectionFailed: boolean;
  availabilityState: AsyncResolverState<TessaEnvironmentAvailability>;
  kind: WorkspaceFieldKind;
  retryAutoKey: () => void;
  status: AvailabilityStatus;
  suggestionState: AsyncResolverState<string[]>;
}): { action: () => void; label: string } | undefined {
  if (input.autoKeySelectionFailed) {
    return {
      action: input.retryAutoKey,
      label: 'Retry automatic workspace key selection',
    };
  }
  if (input.availabilityState.status === 'error') {
    return {
      action: input.availabilityState.retry,
      label: `Retry ${getInputLabel(
        input.kind,
      ).toLowerCase()} availability check`,
    };
  }
  if (input.suggestionState.status === 'error' && input.status === 'taken') {
    return {
      action: input.suggestionState.retry,
      label: `Retry workspace ${input.kind} alternatives`,
    };
  }
  return undefined;
}

function getWorkspaceStatusMessage(input: {
  autoKeySelectionFailed: boolean;
  formatError?: string;
  isSelectingKey: boolean;
  kind: WorkspaceFieldKind;
  status: AvailabilityStatus;
  suggestionState: AsyncResolverState<string[]>;
}): string | undefined {
  if (input.formatError) return input.formatError;
  if (input.isSelectingKey) return 'Finding an available workspace key...';
  if (input.autoKeySelectionFailed) {
    return 'Workspace key could not be selected automatically. Retry or enter a key manually.';
  }
  if (input.status === 'taken') {
    if (input.suggestionState.status === 'error') {
      return `Workspace ${input.kind} alternatives could not be checked. Please retry.`;
    }
    const availabilityMessage = getAvailabilityMessage(
      input.kind,
      input.status,
    );
    if (input.suggestionState.status === 'loading') {
      return `${availabilityMessage} Checking alternatives...`;
    }
    if (
      input.suggestionState.status === 'success' &&
      input.suggestionState.data.length === 0
    ) {
      return `${availabilityMessage} No available alternatives found among the checked candidates. Try another ${input.kind}.`;
    }
  }
  return getAvailabilityMessage(input.kind, input.status);
}

function getWorkspaceStatusIcon(input: {
  autoKeyError: boolean;
  hasFormatError: boolean;
  isSelectingKey: boolean;
  status: AvailabilityStatus;
}): ReactNode {
  if (input.isSelectingKey)
    return getStatusIcon('checking', input.hasFormatError);
  if (input.autoKeyError) return getStatusIcon('error', input.hasFormatError);
  return getStatusIcon(input.status, input.hasFormatError);
}

function getAvailabilityStatus(
  kind: WorkspaceFieldKind,
  state: AsyncResolverState<TessaEnvironmentAvailability>,
): AvailabilityStatus {
  switch (state.status) {
    case 'loading':
      return 'checking';
    case 'success':
      return getFieldValue(kind, state.data) ? 'taken' : 'available';
    case 'error':
      return 'error';
    default:
      return 'idle';
  }
}

function getWorkspaceFormatError(
  kind: WorkspaceFieldKind,
  value: string,
): string | undefined {
  const isValid =
    kind === 'key' ? isValidWorkspaceKey(value) : isValidWorkspaceName(value);

  if (isValid) return undefined;

  return kind === 'key'
    ? 'Workspace key must be 3-8 characters long, start with an uppercase letter, and contain only uppercase letters and numbers without spaces. Examples: TSD or M365CORE'
    : 'Workspace name must be 3-25 characters long. It may contain upper- and lowercase letters, numbers, and spaces without punctuation. It must not start or end with a space. Example: ToolSupport Service Desk';
}

export async function validateWorkspaceField(input: {
  addError: (message: string) => void;
  checkAvailability: (values: {
    environmentKey?: string;
    environmentName?: string;
  }) => Promise<TessaEnvironmentAvailability>;
  kind: WorkspaceFieldKind;
  value: string;
}): Promise<void> {
  const { addError, checkAvailability, kind, value } = input;

  const formatError = getWorkspaceFormatError(kind, value);
  if (formatError) {
    addError(formatError);
    return;
  }

  try {
    const availability = await checkAvailability(
      kind === 'key' ? { environmentKey: value } : { environmentName: value },
    );
    if (getFieldValue(kind, availability)) {
      addError(
        `A workspace with this ${kind} already exists. Choose a different ${kind}.`,
      );
    }
  } catch {
    addError(
      `Workspace ${kind} availability could not be checked. Please try again.`,
    );
  }
}

function getWorkspaceName(
  props: WorkspaceFieldProps,
  kind: WorkspaceFieldKind,
): string {
  if (kind !== 'key') return '';
  const formData = props.formContext?.formData;
  if (
    !formData ||
    typeof formData !== 'object' ||
    !('environmentName' in formData)
  ) {
    return '';
  }
  return typeof formData.environmentName === 'string'
    ? formData.environmentName
    : '';
}

function shouldSelectAutomaticKey(input: {
  attemptedName?: string;
  kind: WorkspaceFieldKind;
  manuallyEdited: boolean;
  previousAutoKey?: string;
  value: string;
  workspaceName: string;
}): boolean {
  return (
    input.kind === 'key' &&
    !input.manuallyEdited &&
    isValidWorkspaceName(input.workspaceName) &&
    (input.value === '' || input.value === input.previousAutoKey) &&
    input.attemptedName !== input.workspaceName
  );
}

function useAutomaticWorkspaceKey(input: {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  kind: WorkspaceFieldKind;
  onChange: WorkspaceFieldProps['onChange'];
  value: string;
  workspaceName: string;
}) {
  const { discoveryApi, fetchApi, kind, onChange, value, workspaceName } =
    input;
  const [isSelectingKey, setIsSelectingKey] = useState(false);
  const [autoKeyError, setAutoKeyError] = useState(false);
  const [autoKeyRetryCount, setAutoKeyRetryCount] = useState(0);
  const hasManuallyEditedKey = useRef(false);
  const autoKeyRef = useRef<string | undefined>(undefined);
  const autoKeyAttemptedNameRef = useRef<string | undefined>(undefined);
  const isAutoKeyChange = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (
      !shouldSelectAutomaticKey({
        attemptedName: autoKeyAttemptedNameRef.current,
        kind,
        manuallyEdited: hasManuallyEditedKey.current,
        previousAutoKey: autoKeyRef.current,
        value,
        workspaceName,
      })
    ) {
      setIsSelectingKey(false);
      return;
    }

    autoKeyAttemptedNameRef.current = workspaceName;
    setAutoKeyError(false);
    const abortController = new AbortController();
    const timeout = window.setTimeout(async () => {
      setIsSelectingKey(true);
      try {
        const client = createTessaEnvironmentAvailabilityClient({
          discoveryApi,
          fetchApi,
        });
        const availableKey = await findAvailableWorkspaceKey({
          checkAvailability: client.check,
          signal: abortController.signal,
          workspaceName,
        });

        if (!abortController.signal.aborted && availableKey) {
          autoKeyRef.current = availableKey;
          isAutoKeyChange.current = true;
          onChangeRef.current(availableKey);
          isAutoKeyChange.current = false;
        } else if (!abortController.signal.aborted) {
          setAutoKeyError(true);
        }
      } catch {
        if (!abortController.signal.aborted) setAutoKeyError(true);
      } finally {
        if (!abortController.signal.aborted) setIsSelectingKey(false);
      }
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      abortController.abort();
    };
  }, [autoKeyRetryCount, discoveryApi, fetchApi, kind, value, workspaceName]);

  const autoKeySelectionFailed =
    kind === 'key' &&
    autoKeyError &&
    !hasManuallyEditedKey.current &&
    isValidWorkspaceName(workspaceName) &&
    (value === '' || value === autoKeyRef.current);

  return {
    autoKeyError,
    autoKeySelectionFailed,
    isSelectingKey,
    markKeyManuallyEdited: () => {
      if (isAutoKeyChange.current) return;
      hasManuallyEditedKey.current = true;
      autoKeyAttemptedNameRef.current = undefined;
      setAutoKeyError(false);
    },
    retryAutoKey: () => {
      autoKeyAttemptedNameRef.current = undefined;
      setAutoKeyError(false);
      setAutoKeyRetryCount(count => count + 1);
    },
  };
}

function WorkspaceFieldMessage(input: {
  hasError: boolean;
  onSuggestionSelected: (suggestion: string) => void;
  rawErrors: string[];
  retry?: { action: () => void; label: string };
  statusMessage?: string;
  suggestions: string[];
}) {
  if (input.rawErrors.length > 0 || !input.statusMessage) return null;
  return (
    <Text as="div" color={input.hasError ? 'danger' : 'primary'}>
      <span>
        <span
          aria-atomic="true"
          aria-live={input.hasError ? 'assertive' : 'polite'}
          role={input.hasError ? 'alert' : 'status'}
        >
          {input.statusMessage}
        </span>
        {input.retry && (
          <span>
            {' '}
            <Button
              aria-label={input.retry.label}
              onClick={input.retry.action}
              size="small"
              type="button"
              variant="secondary"
            >
              Retry
            </Button>
          </span>
        )}
        {input.suggestions.length > 0 && (
          <span>
            {' Try: '}
            {input.suggestions.map(suggestion => (
              <Button
                key={suggestion}
                aria-label={`Use workspace ${suggestion}`}
                onClick={() => input.onSuggestionSelected(suggestion)}
                size="small"
                type="button"
                variant="secondary"
              >
                {suggestion}
              </Button>
            ))}
          </span>
        )}
      </span>
    </Text>
  );
}

function WorkspaceTextField({
  kind,
  ...props
}: WorkspaceFieldProps & { kind: WorkspaceFieldKind }) {
  const discoveryApi = useApi(discoveryApiRef) as DiscoveryApi;
  const fetchApi = useApi(fetchApiRef) as FetchApi;
  const rawErrors = normalizeRawErrors(props.rawErrors);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const value = typeof props.formData === 'string' ? props.formData : '';
  const workspaceName = getWorkspaceName(props, kind);
  const {
    autoKeyError,
    autoKeySelectionFailed,
    isSelectingKey,
    markKeyManuallyEdited,
    retryAutoKey,
  } = useAutomaticWorkspaceKey({
    discoveryApi,
    fetchApi,
    kind,
    onChange: props.onChange,
    value,
    workspaceName,
  });
  const formatErrorForValue = getWorkspaceFormatError(kind, value);
  const formatError = hasInteracted ? formatErrorForValue : undefined;
  const availabilityState = useAsyncResolver<
    WorkspaceAvailabilityContext,
    TessaEnvironmentAvailability
  >(
    async (context, signal) => {
      const client = createTessaEnvironmentAvailabilityClient({
        discoveryApi,
        fetchApi,
      });
      return client.check({
        ...(context.kind === 'key'
          ? { environmentKey: context.value }
          : { environmentName: context.value }),
        signal,
      });
    },
    { kind, value },
    {
      cacheKey: context => `${context.kind}:${context.value}`,
      debounceMs: AVAILABILITY_DEBOUNCE_MS,
      isReady: context =>
        getWorkspaceFormatError(context.kind, context.value) === undefined,
      retry: AVAILABILITY_RETRY,
    },
  );

  const status = getAvailabilityStatus(kind, availabilityState);
  const suggestionState = useAsyncResolver<
    {
      isTaken: boolean;
      kind: WorkspaceFieldKind;
      value: string;
      workspaceName: string;
    },
    string[]
  >(
    async (context, signal) => {
      const client = createTessaEnvironmentAvailabilityClient({
        discoveryApi,
        fetchApi,
      });
      return findAvailableWorkspaceSuggestions({
        checkAvailability: client.check,
        kind: context.kind,
        signal,
        value: context.value,
        workspaceName: context.workspaceName,
      });
    },
    { isTaken: status === 'taken', kind, value, workspaceName },
    {
      cacheKey: context =>
        `${context.kind}:${context.workspaceName}:${context.value}`,
      isReady: context => context.isTaken,
      retry: AVAILABILITY_RETRY,
    },
  );

  const retry = getRetryOption({
    autoKeySelectionFailed,
    availabilityState,
    kind,
    retryAutoKey,
    status,
    suggestionState,
  });
  const suggestions =
    status === 'taken' && !formatError && suggestionState.status === 'success'
      ? suggestionState.data
      : [];
  const statusMessage = getWorkspaceStatusMessage({
    autoKeySelectionFailed,
    formatError,
    isSelectingKey,
    kind,
    status,
    suggestionState,
  });
  const hasError =
    rawErrors.length > 0 ||
    Boolean(formatError) ||
    autoKeySelectionFailed ||
    status === 'taken' ||
    status === 'error';
  const statusIcon = getWorkspaceStatusIcon({
    autoKeyError,
    hasFormatError: Boolean(formatError) || rawErrors.length > 0,
    isSelectingKey,
    status,
  });

  const selectSuggestion = (suggestion: string) => {
    setHasInteracted(true);
    if (kind === 'key') markKeyManuallyEdited();
    props.onChange(suggestion);
    fieldRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  };

  useEffect(() => {
    if (rawErrors.length > 0) {
      fieldRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    }
  }, [rawErrors.length]);

  return (
    <div>
      <TextField
        ref={fieldRef}
        autoFocus={props.autofocus}
        description={
          rawErrors.length === 0 && statusMessage === undefined
            ? props.schema.description
            : undefined
        }
        icon={statusIcon}
        isDisabled={props.disabled}
        isInvalid={hasError}
        isReadOnly={props.readonly}
        isRequired={props.required}
        label={props.schema.title ?? getInputLabel(kind)}
        onBlur={() => {
          setHasInteracted(true);
          props.onBlur(props.idSchema.$id, value);
        }}
        onChange={nextValue => {
          setHasInteracted(true);
          if (kind === 'key') markKeyManuallyEdited();
          props.onChange(nextValue);
        }}
        onFocus={() => {
          props.onFocus(props.idSchema.$id, value);
        }}
        value={value}
      />
      <WorkspaceFieldMessage
        hasError={hasError}
        onSuggestionSelected={selectSuggestion}
        rawErrors={rawErrors}
        retry={retry}
        statusMessage={statusMessage}
        suggestions={suggestions}
      />
    </div>
  );
}

function createWorkspaceField(
  kind: WorkspaceFieldKind,
  name: string,
): FormField {
  return createFormField({
    component: props => <WorkspaceTextField {...props} kind={kind} />,
    name,
    schema: workspaceFieldSchema,
    validation: async (value, field, context) => {
      const formatError = getWorkspaceFormatError(kind, value);
      if (formatError) {
        field.addError(formatError);
        return;
      }

      const discoveryApi = context.apiHolder.get(discoveryApiRef);
      const fetchApi = context.apiHolder.get(fetchApiRef);
      if (!discoveryApi || !fetchApi) {
        return;
      }

      const client = createTessaEnvironmentAvailabilityClient({
        discoveryApi,
        fetchApi,
      });
      await validateWorkspaceField({
        addError: field.addError,
        checkAvailability: client.check,
        kind,
        value,
      });
    },
  });
}

type FormFieldExtension<TName extends string> = ExtensionDefinition<{
  kind: 'scaffolder-form-field';
  name: TName;
  params: { field: () => Promise<FormField> };
}>;

export const TessaWorkspaceNameField = createWorkspaceField(
  'name',
  'TessaWorkspaceNameField',
);
export const TessaWorkspaceKeyField = createWorkspaceField(
  'key',
  'TessaWorkspaceKeyField',
);

export const workspaceNameFieldExtension: FormFieldExtension<'workspace-name-field'> =
  FormFieldBlueprint.make({
    name: 'workspace-name-field',
    params: { field: async () => TessaWorkspaceNameField },
  });

export const workspaceKeyFieldExtension: FormFieldExtension<'workspace-key-field'> =
  FormFieldBlueprint.make({
    name: 'workspace-key-field',
    params: { field: async () => TessaWorkspaceKeyField },
  });
